import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, User, Eye, EyeOff, ArrowLeft, LogIn } from "lucide-react";
import { api, setToken, getToken } from "@/lib/api";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_trading-revolution/artifacts/5op29ky3_REVOLUSI%20NUSATRADE%20PROFILE.png";

export default function AdminLogin() {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (getToken()) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      setToken(data.token);
      nav("/admin/dashboard", { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Login gagal. Cuba lagi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#070405] px-6 py-10 text-white"
      data-testid="admin-login-page"
    >
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_50%_30%,rgba(237,28,36,0.35),transparent_55%),radial-gradient(ellipse_at_80%_80%,rgba(33,64,154,0.25),transparent_60%),linear-gradient(180deg,#0a0507_0%,#140608_50%,#070405_100%)]" />
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.07] mix-blend-screen [background-image:radial-gradient(rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:3px_3px]" />

      <button
        onClick={() => nav("/")}
        className="absolute left-6 top-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-white/70 backdrop-blur-md transition hover:text-white"
        data-testid="back-to-home"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Kembali
      </button>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <img
            src={LOGO_URL}
            alt="Revolusi Nusatrade"
            className="h-16 w-16 rounded-full object-contain drop-shadow-[0_0_18px_rgba(237,28,36,0.55)]"
          />
          <h1
            className="mt-4 text-3xl tracking-[0.12em]"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            data-testid="login-title"
          >
            ADMIN DASHBOARD
          </h1>
          <p
            className="mt-1 text-sm text-white/55"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Revolusi Nusatrade · Traffic Monitor
          </p>
        </div>

        <form
          onSubmit={submit}
          className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md shadow-[0_30px_80px_-30px_rgba(237,28,36,0.45)]"
        >
          <div>
            <label
              className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/55"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Username
            </label>
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <input
                data-testid="login-username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full rounded-xl border border-white/10 bg-black/40 py-3 pl-10 pr-3 text-sm text-white outline-none ring-[#ED1C24]/0 transition focus:border-[#ED1C24]/50 focus:ring-2 focus:ring-[#ED1C24]/30"
                placeholder="RevolusiNusatrade"
              />
            </div>
          </div>

          <div>
            <label
              className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/55"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Password
            </label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <input
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-xl border border-white/10 bg-black/40 py-3 pl-10 pr-10 text-sm text-white outline-none transition focus:border-[#ED1C24]/50 focus:ring-2 focus:ring-[#ED1C24]/30"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 transition hover:text-white"
                data-testid="toggle-password"
                aria-label="Toggle password visibility"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {err && (
            <div
              data-testid="login-error"
              className="rounded-lg border border-[#ED1C24]/40 bg-[#ED1C24]/10 px-3 py-2 text-sm text-[#FDBC3D]"
            >
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#ED1C24] py-3 text-sm font-semibold text-white shadow-[0_10px_30px_-10px_rgba(237,28,36,0.8)] transition hover:scale-[1.01] hover:shadow-[0_15px_40px_-10px_rgba(237,28,36,0.95)] disabled:opacity-60"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <LogIn className="h-4 w-4" />
            {loading ? "Memproses..." : "Masuk Dashboard"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
